import { useMemo } from 'react';
import { planHasWatermark, type WatermarkSummary } from 'fym-shared/src/music24/watermarkUpsell';
import type { Music24PlanTier } from 'fym-shared/src/music24/music24Plans';
import { useLicenseInfo } from '../../../redux/slices/license/hooks/hooks';
import { useIsDemoAccount } from '../../accountSelector/useIsDemoAccount';
import { useMusic24Plan } from '../hooks/useMusic24Plan';
import { useIsUpsellMember } from './useIsUpsellMember';
import { useWatermarkSummary } from './useWatermarkSummary';

export interface WatermarkSurface {
  /** True on Free and the trial when the artist is not the demo: a file surface may show. */
  applies: boolean;
  plan: Music24PlanTier;
  /** A member sees the owner note in the place of each button and price. */
  isMember: boolean;
  summary: WatermarkSummary;
  /** Whole days of trial left; null when the plan is not the trial. */
  trialDaysRemaining: number | null;
}

/**
 * What the always-on file surfaces read (design: trial-upsell a3..a6): the plan, the role, the
 * demo artist and the summary. The automatic modals use `useUpsellFacts` and the shared rules.
 */
export function useWatermarkSurface(): WatermarkSurface {
  const plan = useMusic24Plan();
  const isDemo = useIsDemoAccount();
  const isMember = useIsUpsellMember();
  const licenseInfo = useLicenseInfo();
  const { summary } = useWatermarkSummary();
  const trialDaysRemaining = plan === 'trial' ? licenseInfo.trialDaysRemaining : null;
  return useMemo(
    () => ({ applies: planHasWatermark(plan) && !isDemo, plan, isMember, summary, trialDaysRemaining }),
    [plan, isDemo, isMember, summary, trialDaysRemaining]
  );
}
